import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";

import Header from "./Header";
import { RootState } from "../../store/rootReducer";
import {
  DomSnapshot,
  NewEvent,
  SelectedDomsObj,
} from "../../declarations/interface";
import {
  createNewRecording,
  getDomList,
  actions as recordingActions,
} from "../../slices/recording";
import { getProjectById } from "../../slices/project";
import AppModal from "../../components/AppModal";
import useSnackbar from "../../hooks/useSnackbar";
import CreateNewRecordingBody from "./CreateNewRecordingBody";
import Preview from "../../components/SnapshotView/Preview";
import LinkContainer from "../../components/LinkContainer";
import SuccessIcon from "../../assets/images/success-tick.svg";
import { generateRecordingUrl } from "../../util/UtilService";

const useStyles = makeStyles((theme) => ({
  body: {
    borderTop: `0.5px solid ${theme.palette.primary40.main}`,
  },
  contentCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
}));

const DEFAULT_SELECTED_DOMS_OBJ: SelectedDomsObj = {
  data: {},
  orderedDomIds: [],
};

const CreateRecordingView = () => {
  const classes = useStyles();
  const { projectId } = useParams();
  const { notify, hideNotification } = useSnackbar();
  const [modalOpen, setModalOpen] = useState(false);
  const [recordingName, setRecordingName] = useState("New Recording");
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [fetchedDoms, setFetchedDoms] = useState<DomSnapshot[]>([]);
  const [previewDom, setPreviewDom] = useState<DomSnapshot>();
  const [activeDom, setActiveDom] = useState<DomSnapshot>();
  const [selectedRecordingId, setselectedRecordingId] = useState("");
  const [selectedDomsObj, setSelectedDomsObj] = useState<SelectedDomsObj>(
    DEFAULT_SELECTED_DOMS_OBJ
  );
  const { project } = useSelector((state: RootState) => state.project);
  const { dom_lists, status: recordingStatus } = useSelector(
    (state: RootState) => state.recording
  );
  const dispatch = useDispatch();

  const selectRecording = (recordingId: string) => {
    setselectedRecordingId(recordingId);
  };

  const fetchNextDoms = () => {
    if (!dom_lists) return;
    if (dom_lists.total > dom_lists.page * dom_lists.limit) {
      dispatch(
        getDomList({ id: selectedRecordingId, page: +dom_lists.page + 1 })
      );
    }
  };

  const loadFetchedDoms = () => {
    if (!dom_lists) return;
    setFetchedDoms((prev) => {
      const previousDoms = dom_lists.page === 1 ? [] : prev;
      return [...previousDoms, ...dom_lists.data];
    });
  };

  const handleSave = () => {
    if (!project) return;
    const newRecordingPayload = selectedDomsObj.orderedDomIds.map(
      (dom_uuid) => selectedDomsObj.data[dom_uuid]
    );
    dispatch(
      createNewRecording({
        recordingName,
        projectId: project.project_id,
        newRecordingPayload,
      })
    );
  };

  const selectDom = (dom: DomSnapshot, select: boolean) => {
    setSelectedDomsObj((prev) => {
      if (select) {
        return {
          data: { ...prev.data, [dom.dom_uuid]: dom },
          orderedDomIds: [...prev.orderedDomIds, dom.dom_uuid],
        };
      }
      const { [dom.dom_uuid]: deletedKey, ...rest } = prev.data;
      const newOrderedIds = prev.orderedDomIds.filter(
        (item) => item !== dom.dom_uuid
      );
      return {
        data: rest,
        orderedDomIds: newOrderedIds,
      };
    });
  };

  const selectAllDoms = (select: boolean) => {
    setSelectedDomsObj((prev) => {
      if (select) {
        const domsToAdd: any = {};
        const domIdsToAdd: string[] = [];
        fetchedDoms.map((dom) => {
          if (!prev.data[dom.dom_uuid]) {
            domsToAdd[dom.dom_uuid] = dom;
            domIdsToAdd.push(dom.dom_uuid);
          }
        });
        return {
          data: { ...prev.data, ...domsToAdd },
          orderedDomIds: [...prev.orderedDomIds, ...domIdsToAdd],
        };
      }
      const filteredObject: SelectedDomsObj = { data: {}, orderedDomIds: [] };
      Object.keys(prev.data).forEach((dom_uuid) => {
        fetchedDoms.find((dom) => dom_uuid === dom.dom_uuid);
        if (!fetchedDoms.find((dom) => dom_uuid === dom.dom_uuid)) {
          filteredObject.data[dom_uuid] = prev.data[dom_uuid];
          filteredObject.orderedDomIds.push(dom_uuid);
        }
      });
      return filteredObject;
    });
  };

  const selectDomsHavingEvents = (select: boolean) => {
    setSelectedDomsObj((prev) => {
      const filteredObject: SelectedDomsObj = { data: {}, orderedDomIds: [] };
      Object.keys(prev.data).forEach((dom_uuid) => {
        fetchedDoms.find((dom) => dom_uuid === dom.dom_uuid);
        if (!fetchedDoms.find((dom) => dom_uuid === dom.dom_uuid)) {
          filteredObject.data[dom_uuid] = prev.data[dom_uuid];
          filteredObject.orderedDomIds.push(dom_uuid);
        }
      });
      if (select) {
        fetchedDoms.map((dom) => {
          if (dom.events_lists.length) {
            filteredObject.data[dom.dom_uuid] = dom;
            filteredObject.orderedDomIds.push(dom.dom_uuid);
          }
        });
      }
      return filteredObject;
    });
  };

  const updateEvents = (newEvents: NewEvent[], activeDom: DomSnapshot) => {
    setSelectedDomsObj((prev) => {
      return {
        ...prev,
        data: {
          ...prev.data,
          [activeDom.dom_uuid]: {
            ...prev.data[activeDom.dom_uuid],
            new_events: newEvents,
          },
        },
      };
    });
    setActiveDom({
      ...activeDom,
      new_events: newEvents,
    });
  };

  const removeAllSelectedDoms = () => {
    setSelectedDomsObj(DEFAULT_SELECTED_DOMS_OBJ);
    setActiveDom(undefined);
  };

  const reorderSelectedDoms = (orderedDomIds: string[]) => {
    setSelectedDomsObj((prev) => {
      return { ...prev, orderedDomIds };
    });
  };

  const handlePreviewModalOpen = (dom: DomSnapshot) => {
    setPreviewDom(dom);
    setPreviewModalOpen(true);
  };

  const handlePreviewModalClose = () => {
    setPreviewModalOpen(false);
  };

  const handleModalClose = () => {
    dispatch(recordingActions.clearStatus());
    setModalOpen(false);
    removeAllSelectedDoms();
  };

  const handleSnackbarClose = () => {
    dispatch(recordingActions.clearStatus());
    hideNotification();
  };

  useEffect(() => {
    dispatch(getProjectById(projectId));
  }, [projectId]);

  useEffect(() => {
    if (project?.recordings_lists.length) {
      setselectedRecordingId(project.recordings_lists[0].recording_id);
    }
  }, [project?.recordings_lists]);

  useEffect(() => {
    if (selectedRecordingId) {
      dispatch(getDomList({ id: selectedRecordingId, page: 1 }));
    }
  }, [selectedRecordingId]);

  useEffect(() => {
    fetchNextDoms();
    loadFetchedDoms();
  }, [dom_lists]);

  useEffect(() => {
    if (!recordingStatus) return;
    if (recordingStatus.type === "SUCCESS") {
      setModalOpen(true);
    } else {
      notify({
        message: recordingStatus.message,
        type: recordingStatus.type,
        onClose: handleSnackbarClose,
      });
    }
  }, [recordingStatus]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (selectedDomsObj.orderedDomIds.length) {
        const message = "Are you sure you want to leave?";
        event.returnValue = message;
        return message;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [selectedDomsObj]);

  return (
    <>
      <Box height="100%">
        {project && (
          <Header
            projectId={project.project_id}
            projectName={project?.project_name ?? "PROJECT_NAME"}
            recordingName={"New Recording"}
            onRecordingNameUpdate={setRecordingName}
            handleSave={handleSave}
            selectRecording={selectRecording}
            recordings={project.recordings_lists}
            selectedRecordingId={selectedRecordingId}
            saveEnabled={!!selectedDomsObj.orderedDomIds.length}
          />
        )}
        {project && (
          <Box
            className={classes.body}
            sx={{
              height: `calc(100vh - 138px)`,
            }}
          >
            <CreateNewRecordingBody
              domList={fetchedDoms}
              selectedDoms={selectedDomsObj}
              selectDom={selectDom}
              selectAllDoms={selectAllDoms}
              selectDomsHavingEvents={selectDomsHavingEvents}
              setPreviewDom={handlePreviewModalOpen}
              removeAllSelectedDoms={removeAllSelectedDoms}
              activeDom={activeDom}
              setActiveDom={setActiveDom}
              reorderSelectedDoms={reorderSelectedDoms}
              updateEvents={updateEvents}
            />
          </Box>
        )}
      </Box>
      <AppModal
        open={previewModalOpen}
        onClose={handlePreviewModalClose}
        maxWidth="lg"
      >
        {previewDom && (
          <Box textAlign="center">
            <Preview
              viewType={"RECORDING_VIEW"}
              image={previewDom.dom_thumbnail_path}
              wait_type={previewDom.wait_type ?? "TIME"}
              hotSpotData={{
                height: previewDom.height ?? 0,
                width: previewDom.width ?? 0,
                events: previewDom.events_lists,
              }}
              onMapEvent={() => {
                console.log("On map event clicked...");
              }}
            />
            <Typography variant="subtitle1">{previewDom.dom_uuid}</Typography>
          </Box>
        )}
      </AppModal>
      {recordingStatus && recordingStatus.type === "SUCCESS" && (
        <AppModal open={modalOpen} onClose={handleModalClose}>
          <Box mb={0.5}>
            <Box textAlign="center">
              <img src={SuccessIcon} alt="success" />
            </Box>
            <Typography variant="h3" sx={{ textAlign: "center" }}>
              {recordingStatus.message}
            </Typography>
            <Box display="flex" flexDirection="column" gap="8px" mt={2.75}>
              <LinkContainer
                link={generateRecordingUrl(recordingStatus.context)}
              />
            </Box>
          </Box>
        </AppModal>
      )}
    </>
  );
};

export default CreateRecordingView;
